import React from 'react';
import { Dialog, DialogActions, DialogContent, DialogTitle, Button, IconButton, Chip } from '@mui/material';
import { Close as CloseIcon, CheckCircle as CheckCircleIcon } from '@mui/icons-material';
import { useTranslation, Trans } from 'react-i18next';
import { InlineSpinner } from '../design-system/components/InlineSpinner';

interface LaikaConsentModalProps {
  isOpen: boolean;
  isEnabled: boolean;
  submitting: boolean;
  acceptedAt?: string | null;
  onClose: () => void;
  onEnable: () => void;
  onDisable: () => void;
}

function formatDate(isoDate: string, locale: string): string {
  try {
    return new Date(isoDate).toLocaleDateString(locale, {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  } catch {
    return isoDate;
  }
}

const LaikaConsentModal: React.FC<LaikaConsentModalProps> = ({
  isOpen,
  isEnabled,
  submitting,
  acceptedAt,
  onClose,
  onEnable,
  onDisable,
}) => {
  const { t, i18n } = useTranslation('messaging');

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ style: { borderRadius: 16, overflow: 'hidden' } }}
    >
      <DialogTitle sx={{
        m: 0,
        p: 2.5,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        borderBottom: '1px solid rgba(165, 118, 92, 0.16)',
        fontWeight: 700,
        color: '#3d291c',
      }}>
        {t('laikaConsentModal.title')}
        <IconButton aria-label={t('laikaConsentModal.closeAriaLabel')} onClick={onClose} sx={{ color: '#8b5e48' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3, bgcolor: '#fffaf4' }}>
        <div className="laika-consent-card">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            <p className="laika-consent-eyebrow" style={{ margin: 0 }}>{t('laikaConsentModal.eyebrow')}</p>
            {isEnabled && (
              <Chip
                icon={<CheckCircleIcon style={{ fontSize: 14 }} />}
                label={t('laikaConsentModal.enabledChip')}
                size="small"
                color="success"
                variant="outlined"
              />
            )}
          </div>

          <h3 style={{ marginTop: 8 }}>
            {isEnabled
              ? t('laikaConsentModal.headingEnabled')
              : t('laikaConsentModal.headingDisabled')}
          </h3>

          <p>{t('laikaConsentModal.level1Description')}</p>

          <p>
            <Trans
              i18nKey="laikaConsentModal.level2Description"
              ns="messaging"
              components={{ strong: <strong /> }}
            />
          </p>

          <div style={{
            background: 'rgba(165, 118, 92, 0.08)',
            borderRadius: 8,
            padding: '10px 14px',
            fontSize: 13,
            color: '#5c3d2e',
            marginTop: 8,
            marginBottom: 8,
          }}>
            <Trans
              i18nKey="laikaConsentModal.thirdPartyNotice"
              ns="messaging"
              components={{
                strong: <strong />,
                link: (
                  <a
                    href="https://openai.com/policies/privacy-policy"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: '#8b5e48' }}
                  />
                ),
              }}
            />
          </div>

          <p style={{ fontSize: 13, color: '#64748b' }}>
            <Trans
              i18nKey="laikaConsentModal.disableAnytime"
              ns="messaging"
              components={{ em: <em /> }}
            />
          </p>

          {isEnabled && acceptedAt && (
            <p style={{ fontSize: 12, color: '#94a3b8', marginTop: 4, marginBottom: 0 }}>
              {t('laikaConsentModal.enabledOnDate', { date: formatDate(acceptedAt, i18n.language) })}
            </p>
          )}
        </div>
      </DialogContent>

      <DialogActions sx={{
        p: 2,
        gap: 1,
        bgcolor: '#ffffff',
        borderTop: '1px solid rgba(165, 118, 92, 0.12)',
      }}>
        <Button onClick={onClose} color="inherit" disabled={submitting}>
          {t('laikaConsentModal.close')}
        </Button>
        {isEnabled ? (
          <Button onClick={onDisable} color="warning" disabled={submitting}>
            {t('laikaConsentModal.disableAccess')}
          </Button>
        ) : (
          <Button
            onClick={onEnable}
            variant="contained"
            disabled={submitting}
            startIcon={submitting ? <InlineSpinner color="inherit" aria-hidden /> : undefined}
          >
            {t('laikaConsentModal.enableAccess')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default LaikaConsentModal;
